// components/GazetteAnnouncements.tsx - Updated to use UniversalProvider
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  FaSearch,
  FaFilter,
  FaTimes,
  FaCalendarAlt,
  FaClock,
  FaBuilding,
  FaTag,
  FaExternalLinkAlt,
  FaSync,
  FaChevronDown,
  FaChevronUp,
  FaBookmark,
  FaShare,
  FaCopy,
  FaCheckCircle,
  FaSpinner,
  FaInfoCircle,
  FaEllipsisV,
  FaExpand,
} from 'react-icons/fa';
import { GazetteAnnouncement } from '../types/gazette.types';
import { useGazette } from '../hooks/useGazette';
import { useUniversal } from '../services/UniversalProvider';

interface GazetteAnnouncementsProps {
  initialFilters?: {
    type?: string;
    office?: string;
  };
  className?: string;
  /** Whether to show the fullscreen button */
  showFullscreenButton?: boolean;
  /** Callback when fullscreen is opened */
  onFullscreenOpen?: () => void;
}

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'tender', label: '📋 Tender' },
  { value: 'job', label: '💼 Job Vacancy' },
  { value: 'notice', label: '📢 Notice' },
  { value: 'announcement', label: '📰 Announcement' },
  { value: 'legal', label: '⚖️ Legal' },
];

const OFFICE_OPTIONS = [
  { value: '', label: 'All Offices' },
  { value: 'male', label: 'Malé' },
  { value: 'addu', label: 'Addu City' },
  { value: 'hithadhoo', label: 'Hithadhoo' },
  { value: 'kulhudhuffushi', label: 'Kulhudhuffushi' },
  { value: 'thinadhoo', label: 'Thinadhoo' },
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'government', label: '🏛️ Government' },
  { value: 'private', label: '🏢 Private' },
  { value: 'international', label: '🌍 International' },
];

const API_BASE_URL = 'https://apis.vehidhi.com/vehidhiserver/public';

export function GazetteAnnouncements({ 
  initialFilters, 
  className = '',
  showFullscreenButton = true,
  onFullscreenOpen,
}: GazetteAnnouncementsProps) {
  // ============ UNIVERSAL HOOK ============
  const { 
    fullscreen, 
    dialog, 
    addToast, 
    isDarkMode,
    setLoading,
    isLoading,
    getData,
    setData,
  } = useUniversal();

  // ============ LOCAL STATE ============
  const {
    announcements,
    loading,
    error,
    hasMore,
    refreshing,
    loadMore,
    refresh,
    resetFilters,
    searchTerm,
    setSearchTerm,
    selectedType,
    setSelectedType,
    selectedOffice,
    setSelectedOffice,
    selectedCategory,
    setSelectedCategory,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    openOnly,
    setOpenOnly,
  } = useGazette(initialFilters);

  const [showFilters, setShowFilters] = useState(false);
  const [expandedAnnouncement, setExpandedAnnouncement] = useState<string | null>(null);
  const [bookmarks, setBookmarks] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(searchTerm);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<GazetteAnnouncement | null>(null);
  const [rawHtml, setRawHtml] = useState<string | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);

  // ============ DERIVED STATE ============
  const filteredAnnouncements = useMemo(() => {
    if (!searchTerm) return announcements;
    const term = searchTerm.toLowerCase();
    return announcements.filter(
      (item) =>
        item.title.toLowerCase().includes(term) ||
        item.office.toLowerCase().includes(term) ||
        item.type.toLowerCase().includes(term)
    );
  }, [announcements, searchTerm]);

  // ============ HANDLERS ============
  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearchTerm(searchInput);
    },
    [searchInput, setSearchTerm]
  );

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    setSearchTerm('');
  }, [setSearchTerm]);

  const toggleBookmark = useCallback((id: string) => {
    setBookmarks((prev) => {
      const newBookmarks = new Set(prev);
      if (newBookmarks.has(id)) {
        newBookmarks.delete(id);
      } else {
        newBookmarks.add(id);
      }
      return newBookmarks;
    });
    // Show toast feedback
    const isBookmarked = bookmarks.has(id);
    addToast(
      isBookmarked ? 'Bookmark removed' : 'Bookmark added',
      isBookmarked ? 'warning' : 'success'
    );
  }, [bookmarks, addToast]);

  const copyToClipboard = useCallback(async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      addToast('Copied to clipboard!', 'success');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      addToast('Failed to copy', 'error');
    }
  }, [addToast]);

  const shareAnnouncement = useCallback((item: GazetteAnnouncement) => {
    const shareData = {
      title: item.title,
      text: `${item.title}\n\nOffice: ${item.office}\nType: ${item.type}\nDeadline: ${item.deadline}\nPosted: ${item.date}`,
      url: item.link,
    };

    if (navigator.share && window.innerWidth < 768) {
      navigator.share(shareData).catch(() => {});
    } else {
      const shareText = `${shareData.text}\n\nRead more: ${shareData.url}`;
      copyToClipboard(shareText, item.link);
    }
  }, [copyToClipboard]);

  const getTypeColor = (type: string): string => {
    const colors: Record<string, string> = {
      tender: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      job: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      notice: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
      announcement: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      legal: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    };
    return colors[type.toLowerCase()] || 'bg-gray-100 text-gray-700 dark:bg-gray-700/30 dark:text-gray-300';
  };

  const getTypeEmoji = (type: string): string => {
    const emojis: Record<string, string> = {
      tender: '📋',
      job: '💼',
      notice: '📢',
      announcement: '📰',
      legal: '⚖️',
    };
    return emojis[type.toLowerCase()] || '📄';
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // ============ OPEN ANNOUNCEMENT IN FULLSCREEN ============
  const openAnnouncementInFullscreen = useCallback(async (item: GazetteAnnouncement) => {
    setSelectedAnnouncement(item);
    setLoadingContent(true);
    
    try {
      // Fetch the HTML content
      const response = await fetch(`${API_BASE_URL}/api/fetch-html`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ url: item.link }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch page content');
      }

      // Inject CSS to fix visibility
      const fixedHtml = injectVisibilityCSS(data.html);
      setRawHtml(fixedHtml);
      setLoadingContent(false);

      // Open in fullscreen using UniversalProvider
      fullscreen.open(
        <div className="w-full h-full bg-white dark:bg-gray-900 overflow-auto p-4">
          <div 
            className="w-full h-full"
            dangerouslySetInnerHTML={{ __html: fixedHtml }}
          />
        </div>,
        {
          title: item.title,
          subtitle: `${item.office} • ${item.type}`,
          showBackButton: true,
          showCloseButton: true,
          enableZoom: true,
          enableFitToScreen: true,
          enableFullscreenToggle: true,
          enablePrint: true,
          enableDownload: true,
          enableCopy: true,
          enableShare: true,
          enableBookmark: true,
          enableExternalLink: true,
          shareData: {
            title: item.title,
            text: `Office: ${item.office}\nType: ${item.type}\nDeadline: ${item.deadline}`,
            url: item.link,
          },
          copyText: item.link,
          downloadContent: {
            data: fixedHtml,
            filename: `announcement-${item.title.slice(0, 30)}.html`,
            type: 'html',
          },
          externalLinkUrl: item.link,
          isBookmarked: bookmarks.has(item.link),
          onBookmarkToggle: () => toggleBookmark(item.link),
          backgroundColor: 'bg-white dark:bg-gray-900',
          loadingMessage: 'Loading announcement...',
        }
      );
    } catch (err) {
      console.error('Error fetching announcement:', err);
      setLoadingContent(false);
      addToast('Failed to load announcement', 'error');
      
      // Fallback: Open in dialog with error
      dialog.open({
        title: 'Error Loading Announcement',
        description: 'Failed to load the announcement content. You can try opening it directly.',
        actions: [
          {
            label: 'Open in Browser',
            onClick: () => {
              window.open(item.link, '_blank');
              dialog.close();
            },
            variant: 'primary',
          },
          {
            label: 'Cancel',
            onClick: dialog.close,
            variant: 'secondary',
          },
        ],
      });
    }
  }, [fullscreen, dialog, addToast, bookmarks, toggleBookmark]);

  // ============ OPEN ANNOUNCEMENT IN DIALOG ============
  const openAnnouncementInDialog = useCallback((item: GazetteAnnouncement) => {
    setSelectedAnnouncement(item);
    
    dialog.open({
      title: item.title,
      description: `${item.office} • ${item.type}`,
      maxWidth: 'xl',
      actions: [
        {
          label: 'View Fullscreen',
          onClick: () => {
            dialog.close();
            openAnnouncementInFullscreen(item);
          },
          variant: 'primary',
        },
        {
          label: 'Open in Browser',
          onClick: () => {
            window.open(item.link, '_blank');
            dialog.close();
          },
          variant: 'secondary',
        },
        {
          label: 'Close',
          onClick: dialog.close,
          variant: 'secondary',
        },
      ],
      content: (
        <div className="space-y-4 p-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Office</p>
              <p className="font-medium text-gray-800 dark:text-white flex items-center gap-2">
                <FaBuilding className="text-blue-500" />
                {item.office}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Posted Date</p>
              <p className="font-medium text-gray-800 dark:text-white flex items-center gap-2">
                <FaCalendarAlt className="text-blue-500" />
                {formatDate(item.date)}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Deadline</p>
              <p className="font-medium text-gray-800 dark:text-white flex items-center gap-2">
                <FaClock className="text-red-500" />
                {formatDate(item.deadline)}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Posted</p>
              <p className="font-medium text-gray-800 dark:text-white flex items-center gap-2">
                <FaInfoCircle className="text-gray-500" />
                {item.postedAgo}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <button
              onClick={() => {
                dialog.close();
                openAnnouncementInFullscreen(item);
              }}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <FaExpand size={14} />
              Fullscreen View
            </button>
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <FaExternalLinkAlt size={14} />
              Open Original
            </a>
          </div>
        </div>
      ),
    });
  }, [dialog, openAnnouncementInFullscreen]);

  // ============ OPEN FULLSCREEN VIEW (for the component itself) ============
  const openFullscreenView = useCallback(() => {
    fullscreen.open(
      <div className="w-full h-full bg-gray-50 dark:bg-gray-900 overflow-auto p-4">
        <GazetteAnnouncements 
          showFullscreenButton={false}
          initialFilters={initialFilters}
        />
      </div>,
      {
        title: 'Gazette Announcements',
        subtitle: 'Official announcements from the Maldives Government Gazette',
        showBackButton: true,
        showCloseButton: true,
        enableZoom: true,
        enableFitToScreen: true,
        enableFullscreenToggle: true,
        enablePrint: false,
        enableDownload: false,
        enableCopy: false,
        enableShare: false,
        enableBookmark: false,
        enableExternalLink: false,
        backgroundColor: 'bg-gray-50 dark:bg-gray-900',
      }
    );
    if (onFullscreenOpen) {
      onFullscreenOpen();
    }
  }, [fullscreen, initialFilters, onFullscreenOpen]);

  // ============ INJECT VISIBILITY CSS ============
  const injectVisibilityCSS = (html: string): string => {
    const visibilityCSS = `
      <style>
        body {
          background-color: #ffffff !important;
          color: #1a202c !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
          line-height: 1.6 !important;
          padding: 20px !important;
          margin: 0 !important;
          min-height: 100vh !important;
        }
        * { color: #1a202c !important; }
        h1, h2, h3, h4, h5, h6 { color: #1a202c !important; font-weight: 600 !important; margin-top: 1.5em !important; margin-bottom: 0.5em !important; }
        a { color: #3b82f6 !important; text-decoration: underline !important; }
        a:hover { color: #2563eb !important; }
        p { color: #2d3748 !important; margin-bottom: 1em !important; }
        ul, ol { color: #2d3748 !important; margin: 1em 0 !important; padding-left: 2em !important; }
        li { color: #2d3748 !important; margin-bottom: 0.5em !important; }
        table { border-collapse: collapse !important; width: 100% !important; margin: 1em 0 !important; background: #ffffff !important; }
        th, td { border: 1px solid #e2e8f0 !important; padding: 8px 12px !important; text-align: left !important; color: #2d3748 !important; }
        th { background-color: #f7fafc !important; font-weight: 600 !important; }
        div { color: #2d3748 !important; }
        [style*="background:black"], [style*="background-color:black"], [style*="background:#000"], [style*="background-color:#000"] {
          background: #ffffff !important;
        }
        .bg-black, .bg-dark, .dark-bg { background-color: #ffffff !important; }
        .content, .main, .body, .post, .entry, .article {
          background: #ffffff !important;
          color: #2d3748 !important;
          padding: 20px !important;
        }
        img { max-width: 100% !important; height: auto !important; display: block !important; }
        .container, .container-fluid { padding: 20px !important; background: #ffffff !important; }
        .row, .col, .col-md, .col-sm { background: #ffffff !important; }
      </style>
    `;

    if (html.includes('<head>')) {
      return html.replace('<head>', `<head>${visibilityCSS}`);
    } else if (html.includes('</head>')) {
      return html.replace('</head>', `${visibilityCSS}</head>`);
    } else {
      return `<head>${visibilityCSS}</head>${html}`;
    }
  };

  // ============ RENDER ============
  if (loading && announcements.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        <p className="mt-4 text-gray-500 dark:text-gray-400">Loading announcements...</p>
      </div>
    );
  }

  if (error && announcements.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="text-6xl mb-4">😅</div>
        <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Oops! Something went wrong</h3>
        <p className="text-gray-500 dark:text-gray-400">{error}</p>
        <button
          onClick={refresh}
          className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={`max-w-7xl mx-auto px-4 py-8 ${className}`}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
            <span>📰</span> Gazette Announcements
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Official announcements from the Maldives Government Gazette
          </p>
        </div>
        <div className="flex items-center gap-3">
          {showFullscreenButton && (
            <button
              onClick={openFullscreenView}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl flex items-center gap-2 transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              <FaExpand size={16} />
              <span className="hidden sm:inline">Fullscreen</span>
            </button>
          )}
          <button
            onClick={refresh}
            disabled={refreshing}
            className={`p-2 rounded-xl transition-all ${
              refreshing ? 'animate-spin' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
            } text-gray-600 dark:text-gray-300`}
          >
            <FaSync />
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center gap-2 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <FaFilter className="text-gray-600 dark:text-gray-300" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters</span>
            {showFilters ? <FaChevronUp className="text-sm" /> : <FaChevronDown className="text-sm" />}
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {filteredAnnouncements.length} items
          </span>
        </div>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="relative mb-6">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search announcements by title, office, or type..."
          className="w-full px-5 py-3 pl-12 rounded-2xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:outline-none focus:border-blue-500 dark:focus:border-blue-500 transition-colors"
        />
        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        {searchInput && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <FaTimes />
          </button>
        )}
      </form>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg mb-6 animate-slideDown">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-white focus:outline-none focus:border-blue-500 dark:focus:border-blue-500"
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Office
              </label>
              <select
                value={selectedOffice}
                onChange={(e) => setSelectedOffice(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-white focus:outline-none focus:border-blue-500 dark:focus:border-blue-500"
              >
                {OFFICE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-white focus:outline-none focus:border-blue-500 dark:focus:border-blue-500"
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <div className="flex items-center gap-3 h-10">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={openOnly}
                    onChange={(e) => setOpenOnly(e.target.checked)}
                    className="w-4 h-4 text-blue-500 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Open Only</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-white focus:outline-none focus:border-blue-500 dark:focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-white focus:outline-none focus:border-blue-500 dark:focus:border-blue-500"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={resetFilters}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {filteredAnnouncements.length} announcement{filteredAnnouncements.length !== 1 ? 's' : ''} found
        </span>
        {bookmarks.size > 0 && (
          <span className="text-sm text-blue-500">
            ★ {bookmarks.size} bookmarked
          </span>
        )}
      </div>

      {/* Announcements List */}
      <div className="space-y-4">
        {filteredAnnouncements.map((item) => (
          <div
            key={`${item.link}-${item.page}`}
            className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden ${
              bookmarks.has(item.link) ? 'ring-2 ring-blue-400 dark:ring-blue-500' : 'ring-1 ring-gray-100 dark:ring-gray-700'
            }`}
          >
            {/* Floating Action Buttons - Top Right */}
            <div className="absolute top-3 right-3 z-20 flex items-center gap-1 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl shadow-lg px-1 border border-gray-200/50 dark:border-gray-700/50">
              <button
                onClick={() => toggleBookmark(item.link)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-400 hover:text-blue-500"
                aria-label="Bookmark"
                title={bookmarks.has(item.link) ? 'Remove bookmark' : 'Add bookmark'}
              >
                <FaBookmark
                  className={bookmarks.has(item.link) ? 'text-blue-500 fill-current' : ''}
                  size={14}
                />
              </button>
              <button
                onClick={() => shareAnnouncement(item)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-400 hover:text-blue-500"
                aria-label="Share"
                title="Share"
              >
                <FaShare size={14} />
              </button>
              <button
                onClick={() => copyToClipboard(item.title, item.link)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-400 hover:text-blue-500 relative"
                aria-label="Copy"
                title="Copy to clipboard"
              >
                {copiedId === item.link ? (
                  <FaCheckCircle className="text-green-500" size={14} />
                ) : (
                  <FaCopy size={14} />
                )}
              </button>
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-400 hover:text-blue-500"
                aria-label="Open link"
                title="Open in new tab"
              >
                <FaExternalLinkAlt size={14} />
              </a>
              <div className="w-px h-5 bg-gray-300 dark:bg-gray-600" />
              <button
                onClick={() => openAnnouncementInFullscreen(item)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-400 hover:text-blue-500"
                aria-label="Fullscreen"
                title="Open in fullscreen"
              >
                <FaExpand size={14} />
              </button>
            </div>

            {/* Card Content */}
            <div 
              className="w-full flex flex-col p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              onClick={() => openAnnouncementInDialog(item)}
            >
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(item.type)}`}>
                  {getTypeEmoji(item.type)} {item.type}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <FaBuilding className="text-xs" />
                  {item.office}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <FaClock className="text-xs" />
                  {item.postedAgo}
                </span>
              </div>
              
              <h3 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-white hover:text-blue-500 dark:hover:text-blue-400 transition-colors line-clamp-2 pr-4">
                {item.title}
              </h3>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <FaCalendarAlt className="text-gray-400" />
                  <span>Posted: {formatDate(item.date)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <FaClock className="text-gray-400" />
                  <span>Deadline: {formatDate(item.deadline)}</span>
                </div>
              </div>
            </div>

            {/* Expand/Collapse Toggle - Bottom */}
            <button
              onClick={() => setExpandedAnnouncement(expandedAnnouncement === item.link ? null : item.link)}
              className="absolute bottom-3 right-3 text-sm text-blue-500 hover:text-blue-600 transition-colors flex items-center gap-1 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-2 py-1 rounded-lg border border-gray-200/50 dark:border-gray-700/50"
            >
              {expandedAnnouncement === item.link ? (
                <><FaChevronUp size={12} /> Hide</>
              ) : (
                <><FaChevronDown size={12} /> More</>
              )}
            </button>

            {/* Expanded Content */}
            {expandedAnnouncement === item.link && (
              <div className="px-6 pb-6 pt-2 border-t border-gray-200 dark:border-gray-700 animate-slideDown">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Type:</span>
                      <span className="ml-2 text-gray-700 dark:text-gray-300">{item.type}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Office:</span>
                      <span className="ml-2 text-gray-700 dark:text-gray-300">{item.office}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Posted Date:</span>
                      <span className="ml-2 text-gray-700 dark:text-gray-300">{formatDate(item.date)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Deadline:</span>
                      <span className="ml-2 text-gray-700 dark:text-gray-300">{formatDate(item.deadline)}</span>
                    </div>
                    <div className="sm:col-span-2">
                      <span className="text-gray-500 dark:text-gray-400">Posted:</span>
                      <span className="ml-2 text-gray-700 dark:text-gray-300">{item.postedAgo}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => openAnnouncementInFullscreen(item)}
                      className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <FaExpand size={14} />
                      Open Fullscreen
                    </button>
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FaExternalLinkAlt size={14} />
                      View Original
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Load More */}
      {hasMore && (
        <div className="text-center mt-8">
          <button
            onClick={loadMore}
            disabled={loading}
            className="px-8 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </button>
        </div>
      )}

      {/* Empty State */}
      {filteredAnnouncements.length === 0 && !loading && (
        <div className="text-center py-20">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No announcements found</h3>
          <p className="text-gray-500 dark:text-gray-400">Try adjusting your filters or search terms</p>
          <button
            onClick={resetFilters}
            className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
          >
            Reset Filters
          </button>
        </div>
      )}
    </div>
  );
}